import { Form } from 'antd';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { VoucherBody } from 'services/api/voucher';
import { Modal } from 'antd';
import { voucherVo2FormValuesAdapter } from 'views/Conversation/Sider/VoucherDrawer/utils';
import useVoucherValues from 'views/Conversation/Sider/VoucherDrawer/useVoucherValues';
import { useMemoizedFn } from 'ahooks';
import { useVoucherFormSubmit } from 'views/Conversation/Sider/VoucherDrawer/useVoucherFormSubmit';
import { VoucherFormBody } from 'views/Conversation/Sider/VoucherDrawer/VoucherFormBody';

const { useForm } = Form;

export interface CouponModalProps {
  conversation: Conversation.Conversation;
  visible: boolean;
  title?: string;
  confirmText?: string;
  onCancel: (status?: Conversation.Voucher.Status) => void;
  afterSubmit?: () => void;
}

interface ExtraFormValues extends Conversation.Voucher.Vo {
  displayChannel: number;
}

export type CouponModalRef = {
  form: ReturnType<typeof useForm>[0];
  setFormValues: (values: Conversation.Voucher.Vo) => void;
  setStatus: (status: Conversation.Voucher.Status | undefined) => void;
};

const CouponModal = (props: CouponModalProps, ref: React.Ref<CouponModalRef>) => {
  const { t } = useTranslate();
  const {
    conversation,
    visible,
    title = t('scene.chatWorkspace.couponForm.title.create'),
    confirmText = t('common.action.confirm'),
    onCancel,
    afterSubmit,
  } = props;
  const { storeId, channel } = conversation;
  const [form] = useForm<VoucherBody>();
  const [status, setStatus] = useState<Conversation.Voucher.Status | undefined>();
  const [voucherValues, setVoucherValues] = useState<ExtraFormValues>();
  const { clearVoucherFormCache } = useVoucherValues();
  const { dynamicRules, submitLoading, submitCreateFormValues, submitEditFormValues } =
    useVoucherFormSubmit();

  const submitVoucher = useMemoizedFn(async (formValues: any) => {
    if (status && voucherValues) {
      const result = await submitEditFormValues(
        voucherValues.internalVoucherId,
        formValues,
        storeId,
        channel,
      );
      if (result) {
        clearVoucherFormCache(storeId);
        onCancel?.();
        afterSubmit?.();
      }
    } else {
      const result = await submitCreateFormValues(formValues, storeId, channel);
      if (result) {
        clearVoucherFormCache(storeId);
        onCancel?.('ALL');
        afterSubmit?.();
      }
    }
  });

  const setFormValues = (values: Conversation.Voucher.Vo) => {
    form?.resetFields();
    const formValues = voucherVo2FormValuesAdapter(values) as unknown as ExtraFormValues;
    setVoucherValues(formValues);
    form?.setFieldsValue(formValues as any);
  };

  useImperativeHandle(ref, () => ({
    form,
    setFormValues,
    setStatus,
  }));

  useEffect(() => {
    if (!visible) {
      setVoucherValues(undefined);
    }
  }, [visible]);

  useEffect(() => {
    setTimeout(() => {
      form?.validateFields();
    }, 200);
  }, [dynamicRules, form]);

  return (
    <Modal
      title={title}
      visible={visible}
      onCancel={() => onCancel?.()}
      destroyOnClose
      width={600}
      centered
      bodyStyle={{
        position: 'relative',
        padding: '0 20px 0',
        overflow: 'auto',
        maxHeight: '80vh',
        display: 'flex',
        flexDirection: 'column',
      }}
      zIndex={1040}
      okButtonProps={{
        loading: submitLoading,
      }}
      onOk={() => {
        form.submit();
      }}
    >
      <div className="[p-20px]">
        <VoucherFormBody
          conversation={conversation}
          form={form}
          status={status}
          submitVoucher={submitVoucher}
          submitLoading={submitLoading}
          confirmText={confirmText}
          dynamicRules={dynamicRules}
          showFooter={false}
        />
      </div>
    </Modal>
  );
};

export default forwardRef(CouponModal);
